import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validatePublications } from './validation';
import { MONTHS, YEAR_RANGE, parseDateString } from './constants';

const PublicationsSection = memo(({ 
    initialData, 
    authAxios, 
    setError, 
    onUpdate 
}) => {
    const [publications, setPublications] = useState(initialData || []);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);

    useEffect(() => {
        if (initialData) {
            // Parse dates from API (YYYY-MM-DD) to month/year components
            const parsedData = initialData.map(pub => {
                const dateParsed = parseDateString(pub.publication_date);
                return {
                    ...pub,
                    pub_month: dateParsed.month,
                    pub_year: dateParsed.year,
                };
            });
            setPublications(parsedData);
        }
    }, [initialData]);

    const addPublication = useCallback(() => {
        setPublications(prev => [...prev, {
            id: Date.now(),
            title: '',
            publisher: '',
            pub_month: '',
            pub_year: '',
            url: '',
            authors: '',
            description: '',
        }]);
    }, []);

    const updatePublication = useCallback((id, field, value) => {
        setPublications(prev => prev.map(pub => 
            pub.id === id ? { ...pub, [field]: value } : pub
        ));
    }, []);

    const removePublication = useCallback(async (id) => {
        if (typeof id === 'number' && id > 1000000000000) {
            setPublications(prev => prev.filter(pub => pub.id !== id));
            return;
        }
        
        try {
            await authAxios.delete(`/publications/${id}/`);
            setPublications(prev => prev.filter(pub => pub.id !== id));
        } catch (error) {
            console.error('Failed to remove publication:', error);
            setError?.('Failed to remove publication');
        }
    }, [authAxios, setError]);

    // Format date for API (YYYY-MM-DD)
    const formatDate = (month, year) => {
        if (!month || !year) return null;
        return `${year}-${month.padStart(2, '0')}-01`;
    };

    const savePublicationEntry = async (pub) => {
        const payload = {
            title: pub.title,
            publisher: pub.publisher,
            publication_date: formatDate(pub.pub_month, pub.pub_year),
            url: pub.url,
            authors: pub.authors,
            description: pub.description,
        };

        if (typeof pub.id === 'number' && pub.id > 1000000000000) {
            const response = await authAxios.post('/publications/', payload);
            return response.data;
        } else {
            const response = await authAxios.patch(`/publications/${pub.id}/`, payload);
            return response.data;
        }
    };

    const handleSaveAll = async () => {
        const errors = validatePublications(publications);
        if (errors.length > 0) {
            setShowErrors(true);
            setError?.('Please fill in required fields for all publications');
            return;
        }


        setSaving(true);

        try {
            const savedEntries = await Promise.all(
                publications.map(pub => savePublicationEntry(pub))
            );
            // Parse dates from response
            const parsedEntries = savedEntries.map(pub => {
                const dateParsed = parseDateString(pub.publication_date);
                return {
                    ...pub,
                    pub_month: dateParsed.month,
                    pub_year: dateParsed.year,
                };
            });
            setPublications(parsedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            setError?.('Failed to save publications');
        } finally {
            setSaving(false);
        }
    };

    const hasFieldError = (pubIndex, field) => {
        if (!showErrors) return false;
        const pub = publications[pubIndex];
        if (field === 'title' && !pub?.title?.trim()) return true;
        return false;
    };

    // Dropdown styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    Publications
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={addPublication} startIcon={<AddIcon />} sx={{ color: '#4f46e5', textTransform: 'none', fontWeight: 500 }}>
                        Add Publication
                    </Button>
                    <Button variant="contained" onClick={handleSaveAll} disabled={saving || publications.length === 0}
                        sx={{ bgcolor: '#4f46e5', color: 'white', textTransform: 'none', fontWeight: 500, borderRadius: '6px', px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                    </Button>
                </Box>
            </Box>

            {publications.length === 0 ? (
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>No publications added.</Typography>
            ) : (
                publications.map((pub, index) => (
                    <Box key={pub.id} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 3, mb: 2, position: 'relative' }}>
                        <IconButton size="small" onClick={() => removePublication(pub.id)} sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                                    Title<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <TextField fullWidth size="small" placeholder="Publication title" value={pub.title || ''}
                                    onChange={(e) => updatePublication(pub.id, 'title', e.target.value)}
                                    sx={hasFieldError(index, 'title') ? getInputStyles(true, true) : inputStyles} />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Publisher</Typography>
                                <TextField fullWidth size="small" placeholder="Publisher name" value={pub.publisher || ''}
                                    onChange={(e) => updatePublication(pub.id, 'publisher', e.target.value)} sx={inputStyles} />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Publication Date</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={pub.pub_month || ''}
                                        onChange={(e) => updatePublication(pub.id, 'pub_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={pub.pub_year || ''}
                                        onChange={(e) => updatePublication(pub.id, 'pub_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Authors</Typography>
                                <TextField fullWidth size="small" placeholder="Authors" value={pub.authors || ''}
                                    onChange={(e) => updatePublication(pub.id, 'authors', e.target.value)} sx={inputStyles} />
                            </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>URL</Typography>
                            <TextField fullWidth size="small" placeholder="https://..." value={pub.url || ''}
                                onChange={(e) => updatePublication(pub.id, 'url', e.target.value)} sx={inputStyles} />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Description</Typography>
                            <TextField fullWidth size="small" multiline rows={2} placeholder="Brief description..." value={pub.description || ''}
                                onChange={(e) => updatePublication(pub.id, 'description', e.target.value)} sx={inputStyles} />
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
});

PublicationsSection.displayName = 'PublicationsSection';
export default PublicationsSection;
